import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, MapPin, Car, CircleDot, Flag, Search } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";

const inputCls =
  "w-full bg-[#1E2128] border border-[#2E3340] focus:border-amber-500/60 outline-none px-4 py-3.5 text-sm font-medium text-zinc-100 placeholder:text-zinc-500 transition-colors duration-300";

const STEPS = [
  { key: "reserved", label: "Reservation received", icon: CircleDot },
  { key: "confirmed", label: "Chauffeur confirmed", icon: Check },
  { key: "en_route", label: "Chauffeur en route", icon: Car },
  { key: "arrived", label: "Arrived at pickup", icon: MapPin },
  { key: "aboard", label: "Journey underway", icon: Car },
  { key: "completed", label: "Journey complete", icon: Flag },
];

const stepReached = (booking, key) => {
  const { status, chauffeur_status } = booking;
  if (status === "cancelled") return false;
  if (key === "reserved") return true;
  if (key === "confirmed") return ["confirmed", "completed"].includes(status);
  if (key === "completed") return status === "completed";
  if (status === "completed") return true;
  const order = ["en_route", "arrived", "aboard"];
  return order.indexOf(chauffeur_status) >= order.indexOf(key);
};

export default function Track() {
  const [reference, setReference] = useState("");
  const [email, setEmail] = useState("");
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(false);
  const pollRef = useRef(null);

  const lookup = async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const { data } = await api.post("/track", { reference, email });
      setBooking(data);
    } catch (err) {
      if (!quiet) {
        setBooking(null);
        toast.error(formatApiError(err));
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    if (!booking) return;
    pollRef.current = setInterval(() => lookup(true), 20000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking?.reference]);

  const submit = (e) => {
    e.preventDefault();
    lookup();
  };

  return (
    <main className="min-h-screen bg-[#0F1012] grain" data-testid="track-page">
      <header className="border-b border-[#2E3340]">
        <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
          <Logo />
          <Link
            to="/"
            className="font-mono-lux text-[10px] font-semibold uppercase tracking-[0.3em] text-zinc-400 hover:text-amber-400 transition-colors"
          >
            ← Back
          </Link>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-6 py-16 lg:py-24">
        <p className="font-mono-lux text-[11px] font-semibold uppercase tracking-[0.4em] text-amber-500">Track My Ride</p>
        <h1 className="font-serif-lux font-normal text-4xl sm:text-5xl text-amber-50 mt-3">
          Your chauffeur, <span className="gold-text italic">in real time.</span>
        </h1>

        <form onSubmit={submit} className="mt-10 space-y-4" data-testid="track-form">
          <input
            value={reference}
            onChange={(e) => setReference(e.target.value.toUpperCase())}
            placeholder="Booking reference — e.g. AUR-1A2B3C"
            className={inputCls}
            data-testid="track-reference-input"
            required
          />
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email used for the reservation"
            className={inputCls}
            data-testid="track-email-input"
            required
          />
          <button
            type="submit"
            disabled={loading}
            data-testid="track-submit-button"
            className="w-full flex items-center justify-center gap-2 font-mono-lux text-[11px] font-bold uppercase tracking-[0.3em] px-8 py-4 bg-amber-500 text-black hover:bg-amber-300 disabled:opacity-40 transition-colors"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <Search size={14} />} Follow my journey
          </button>
        </form>

        {booking && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 border border-[#2E3340] bg-[#1E2128] p-7"
            data-testid="track-result"
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-mono-lux text-[10px] tracking-[0.25em] text-amber-500" data-testid="track-ref">{booking.reference}</p>
                <p className="font-serif-lux text-2xl text-amber-50 mt-1">{booking.vehicle_name}</p>
                <p className="text-xs text-zinc-400 mt-1 font-medium">
                  {booking.date} at {booking.time} · {booking.pickup}
                  {booking.dropoff ? ` → ${booking.dropoff}` : " · at your direction"}
                </p>
                {booking.stops?.length > 0 && (
                  <p className="text-[11px] text-zinc-500 mt-1">Stops: {booking.stops.join(" · ")}</p>
                )}
                {booking.flight_number && (
                  <p className="text-[11px] text-zinc-500 mt-1">Flight {booking.flight_number} — tracked by your concierge</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1.5">
                {booking.deposit_paid && (
                  <span className="font-mono-lux text-[9px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-600/50 px-2 py-0.5">Deposit paid</span>
                )}
                {booking.balance_paid && (
                  <span className="font-mono-lux text-[9px] uppercase tracking-[0.15em] text-emerald-400 border border-emerald-600/50 px-2 py-0.5">Settled</span>
                )}
              </div>
            </div>

            {booking.status === "cancelled" ? (
              <p className="mt-6 border border-red-700/40 bg-red-950/20 text-red-300 text-sm px-4 py-3" data-testid="track-cancelled">
                This reservation has been cancelled. If that's unexpected, call the concierge at +1 (786) 874-0320.
              </p>
            ) : (
              <ol className="mt-8 space-y-0" data-testid="track-timeline">
                {STEPS.map((s, i) => {
                  const reached = stepReached(booking, s.key);
                  const current =
                    reached &&
                    (i === STEPS.length - 1 ? booking.status === "completed" : !stepReached(booking, STEPS[i + 1].key));
                  return (
                    <li key={s.key} className="flex gap-4" data-testid={`track-step-${s.key}`}>
                      <div className="flex flex-col items-center">
                        <span
                          className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors duration-500 ${
                            current
                              ? "border-amber-500 bg-amber-500 text-black"
                              : reached
                                ? "border-amber-500/60 text-amber-400"
                                : "border-zinc-800 text-zinc-700"
                          }`}
                        >
                          <s.icon size={13} strokeWidth={2} />
                        </span>
                        {i < STEPS.length - 1 && <span className={`w-px flex-1 min-h-6 ${reached ? "bg-amber-500/40" : "bg-zinc-800"}`} />}
                      </div>
                      <p
                        className={`pt-1.5 text-sm font-medium pb-6 ${
                          current ? "text-amber-300" : reached ? "text-zinc-300" : "text-zinc-600"
                        }`}
                      >
                        {s.label}
                        {current && booking.status !== "completed" && (
                          <span className="ml-2 inline-block w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse align-middle" />
                        )}
                      </p>
                    </li>
                  );
                })}
              </ol>
            )}
            <p className="mt-2 text-[11px] text-zinc-600 font-medium">Updates automatically every 20 seconds.</p>
          </motion.div>
        )}
      </div>
    </main>
  );
}
